/* START_MODULE_CONTRACT
 * PURPOSE: Axios HTTP client с auth-интерсепторами, feature-override header и прозрачной ротацией access token по 401.
 * SCOPE: Создание axios instance, Bearer, X-Feature-Overrides (ADMIN), обработка 401 с single-flight refresh.
 * DEPENDS: M-FE-API, M-FE-FEATURE-TOGGLE
 * LINKS: M-FE-API, V-M-FE-API, UC-022
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * apiClient - axios instance с request/response интерсепторами (default export).
 * processQueue - разрешает/отклоняет запросы, ожидавшие завершения refresh.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-08-10 - UC-022: attach X-Feature-Overrides for ADMIN.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: function contracts, semantic blocks и branch-logging для refresh-flow.
 * END_CHANGE_SUMMARY
 */

import axios from 'axios';
import config from '../config';
import {
  FEATURE_OVERRIDE_HEADER,
  getFeatureOverridesHeaderValue,
} from '../features/featureToggle/featureToggleStorage';

const apiClient = axios.create({
  baseURL: config.baseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((reqConfig) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }

  // START_BLOCK_ATTACH_FEATURE_OVERRIDES
  const overridesHeader = getFeatureOverridesHeaderValue();
  if (overridesHeader) {
    reqConfig.headers[FEATURE_OVERRIDE_HEADER] = overridesHeader;
    console.debug('[FeApi][requestInterceptor][BLOCK_ATTACH_FEATURE_OVERRIDES] header attached');
  }
  // END_BLOCK_ATTACH_FEATURE_OVERRIDES

  return reqConfig;
});

let isRefreshing = false;
let failedQueue = [];

/* START_METHOD_processQueue */
/* START_CONTRACT: processQueue
 * PURPOSE: Разрешить или отклонить все запросы, которые ждали завершения текущей ротации токена.
 * INPUTS: { error: Error|null - ошибка refresh или null при успехе, token: string|null - новый access token }
 * OUTPUTS: { void }
 * SIDE_EFFECTS: Резолвит/реджектит промисы из failedQueue; очищает failedQueue.
 * LINKS: M-FE-API
 * END_CONTRACT: processQueue
 */
function processQueue(error, token = null) {
  for (const { resolve, reject } of failedQueue) {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  }
  failedQueue = [];
}
/* END_METHOD_processQueue */

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // START_BLOCK_PASS_THROUGH
    if (error.response?.status !== 401 || originalRequest._retry) {
      console.debug('[FeApi][responseInterceptor][BLOCK_PASS_THROUGH] non-401 or already retried');
      return Promise.reject(error);
    }
    // END_BLOCK_PASS_THROUGH

    // START_BLOCK_QUEUE_WHILE_REFRESHING
    if (isRefreshing) {
      console.debug('[FeApi][responseInterceptor][BLOCK_QUEUE_WHILE_REFRESHING] queued pending refresh');
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }
    // END_BLOCK_QUEUE_WHILE_REFRESHING

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token');
      }

      const { data } = await axios.post(
        `${config.baseUrl}/api/auth/refresh`,
        { refreshToken },
      );

      const newAccessToken = data.accessToken;
      const newRefreshToken = data.refreshToken;

      localStorage.setItem('accessToken', newAccessToken);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }

      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      processQueue(null, newAccessToken);

      // START_BLOCK_REFRESH_SUCCESS
      console.debug('[FeApi][responseInterceptor][BLOCK_REFRESH_SUCCESS] token rotated, retrying request');
      // END_BLOCK_REFRESH_SUCCESS
      return apiClient(originalRequest);
    } catch (refreshError) {
      // START_BLOCK_REFRESH_FAIL
      processQueue(refreshError, null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      console.warn('[FeApi][responseInterceptor][BLOCK_REFRESH_FAIL] refresh failed, redirecting to login');
      window.location.href = '/login';
      return Promise.reject(refreshError);
      // END_BLOCK_REFRESH_FAIL
    } finally {
      isRefreshing = false;
    }
  },
);

export default apiClient;
